import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { generateSummaryFields, dashboardForm, generateChangeDriversFields, generateAnomalyRadarFields, generatePatternFinderFields, generateTimelineOfEventsFields, generateProposeActionsFields, generateBriefingDataFields, aiSightsSwitch } from '../utils'
import { VuiActionBoxRow, VuiFormGroup } from '@topkamera/viso-ui';
import { KeyFinding } from './KeyFinding';

export const BriefingData = ({
  color = 'accent2',
  values = {},
  onChange = () => null
}) => {
  const formProps = useMemo(() => ({ color: color, fieldsGap: 8 }), [color])
  const [expandedLabel, setExpandedLabel] = useState('')

  const onFormChange = useCallback((fieldId, data, type) => {

    // VuiForm may emit onChange on mount/initialization; ignore those to avoid update loops
    if (!fieldId) return

    onChange({ [fieldId]: data })
  }, [onChange])

  const onUpdateSwitch = useCallback((data,) => {
    onFormChange('briefing', data)
  }, [onChange])

  const briefingDataFields = useMemo(
    () => generateBriefingDataFields({ data: values, color }),
    [values, color]
  )

  const summaryFields = useMemo(
    () => generateSummaryFields({ data: values, color }),
    [values, color]
  )

  const changeDriversFields = useMemo(
    () => generateChangeDriversFields({ data: values, color }),
    [values, color]
  )

  const anomalyRadarFields = useMemo(
    () => generateAnomalyRadarFields({ data: values, color }),
    [values, color]
  )

  const patternFinderFields = useMemo(
    () => generatePatternFinderFields({ data: values, color }),
    [values, color]
  )

  const timelineOfEventsFields = useMemo(
    () => generateTimelineOfEventsFields({ data: values, color }),
    [values, color]
  )

  const proposeActionsFields = useMemo(
    () => generateProposeActionsFields({ data: values, color }),
    [values, color]
  )

  const rows = useMemo(() => ([
    {
      modeId: 'summary',
      expandedContent: dashboardForm({
        description: 'Get the headline story from the selected charts, what matters most right now.',
        fields: summaryFields,
        onFormChange: (data, field, val) => onFormChange('briefing', { ...values.briefing, 'summary': { ...values.briefing?.summary, ...data } }, field, val),
        formProps
      }),
      switchLabel: 'Summary'
    },
    {
      modeId: 'changeDrivers',
      switchLabel: 'Change Drivers',
      expandedContent: dashboardForm({
        description: 'See what’s driving the shifts, which segment caused the change and by how much.',
        fields: changeDriversFields,
        onFormChange: (data, field, val) => onFormChange('briefing', { ...values.briefing, 'changeDrivers': { ...values.briefing?.changeDrivers, ...data } }, field, val),
        formProps
      }),
    },
    {
      modeId: 'anomalyRadar',
      switchLabel: 'Anomaly Radar',
      expandedContent: dashboardForm({
        description: 'Highlight unusual spikes, drops, or outliers in the data and when they occurred.',
        fields: anomalyRadarFields,
        onFormChange: (data, field, val) => onFormChange('briefing', { ...values.briefing, 'anomalyRadar': { ...values.briefing?.anomalyRadar, ...data } }, field, val),
        formProps
      }),
    },
    {
      modeId: 'patternFinder',
      switchLabel: 'Pattern Finder',
      expandedContent: dashboardForm({
        description: 'Identify trends, recurring cycles, or structural changes over time and describe them.',
        fields: patternFinderFields,
        onFormChange: (data, field, val) => onFormChange('briefing', { ...values.briefing, 'patternFinder': { ...values.briefing?.patternFinder, ...data } }, field, val),
        formProps
      }),
    },
    {
      modeId: 'timelineOfEvents',
      switchLabel: 'Timeline of Events',
      expandedContent: dashboardForm({
        description: 'Create a time-ordered narrative of the most important moments.',
        fields: timelineOfEventsFields,
        onFormChange: (data) => onFormChange('briefing', { ...values.briefing, 'timelineOfEvents': { ...values.briefing?.timelineOfEvents, ...data } }),
        formProps
      }),
    },
    {
      modeId: 'proposeActions',
      switchLabel: 'Propose Actions',
      expandedContent: dashboardForm({
        description: 'Suggest practical next steps based on the insights, based on observed changes and risks.',
        fields: proposeActionsFields,
        onFormChange: (data) => onFormChange('briefing', { ...values.briefing, 'proposeActions': { ...values.briefing?.proposeActions, ...data } }),
        formProps
      }),
    }
  ]), [summaryFields, changeDriversFields, anomalyRadarFields, patternFinderFields, timelineOfEventsFields, proposeActionsFields, onFormChange, formProps])

  return (
    <>
      <div className='gap-8'>
        {values?.mode?.[0]?.value === 'keyFinding' && <KeyFinding
          onChange={(data) => onFormChange('keyFinding', data)}
          values={values}
          color={color} />}
        {values?.mode?.[0]?.value === 'briefing' &&
          <>
            {dashboardForm({
              description: '',
              fields: briefingDataFields,
              onFormChange: (data) => onFormChange('briefing', { ...values.briefing, ...data }),
              formProps
            })}
            <VuiActionBoxRow
              color={'accent2'}
              isCard
              rows={rows}
              rowClick={(row) => setExpandedLabel(row.modeId)}
              isExpanded={(row) => row.modeId === expandedLabel}
              action={{
                type: 'button',
                onClick: (row) => setExpandedLabel(row.modeId)
              }}
              cellDefs={[
                {
                  key: 'modeId',
                  color,
                  formatter: (key, row) => aiSightsSwitch({ row, values: values, setValues: onUpdateSwitch, setExpandedLabel, color })
                }
              ]}
            />
          </>
        }
      </div>
    </>
  )
}
